import DescriptionIcon from "@mui/icons-material/Description";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import PetsIcon from "@mui/icons-material/Pets";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CharCountField from "../../../../components/form/CharCountField";
import { SingleBoxContext } from "../../../../contexts/BoxProvider";
import { mandatory, useIsMobile } from "../../../../utils/Functions";
import BoxPetSelector from "./components/BoxPetSelector";

const labels = {
  therapyName: "title",
};
const titleStyles = {
  iconColor: "primary",
  fontSize: "h6",
  fontSizeMobile: "body1",
};

export default function TherapyGeneral({ hidePetSelector = false }) {
  const { getPetsInBox } = React.useContext(SingleBoxContext);
  const petsInBox = getPetsInBox();
  const shouldHidePetSelector = hidePetSelector || petsInBox?.length === 1;
  return (
    <Stack spacing={3}>
      {shouldHidePetSelector ? null : <PetSelection />}
      <TherpyTitle />
      <Card variant="elevation">
        <CardContent>
          <Stack spacing={3}>
            <AdministrationInstructions />
            <Divider />
            <TherapyDescription />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

function TherapyDescription() {
  const { t } = useTranslation();

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <DescriptionIcon color={titleStyles.iconColor} />
        <Title string="TherapyDescription" />
      </Stack>

      <CharCountField
        name="description"
        label="Description"
        maxChar={200}
        multiline
        maxSize={2}
        placeholder={t("TherapyDescriptionPlaceHolder")}
        fullWidth
        helperText={t("TherapyDescriptionHelperText")}
        InputProps={{
          sx: {
            fontSize: "0.875rem",
            lineHeight: 1.5,
          },
        }}
      />
    </Box>
  );
}

function PetSelection() {
  const { getPetsInBox } = React.useContext(SingleBoxContext);

  return (
    <Card variant="elevation">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <PetsIcon color={titleStyles.iconColor} />
            <Title string="SelectPetFromBox" />
          </Stack>
          <BoxPetSelector
            noNewPets={true}
            pets={getPetsInBox()}
            key={"add-pet-to-therapy-pet-selector"}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function AdministrationInstructions() {
  const { t } = useTranslation();

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <DescriptionIcon color={titleStyles.iconColor} />
        <Title string="AdministrationInstructions" />
      </Stack>

      <CharCountField
        name="administration"
        label="InstructionsExample"
        maxChar={200}
        multiline
        maxSize={2}
        placeholder={t("WayOfAdministrationPlaceholder")}
        fullWidth
        helperText={t("WayOfAdministrationHelperText")}
        InputProps={{
          sx: {
            fontSize: "0.875rem",
            lineHeight: 1.5,
          },
        }}
      />
    </Box>
  );
}

function TherpyTitle() {
  const { t } = useTranslation();
  const methods = useFormContext();

  return (
    <Card variant="elevation">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MedicalServicesIcon color={titleStyles.iconColor} />
            <Title string="TherapyDetails" />
          </Stack>

          <TextField
            fullWidth
            id="therapy-name"
            inputProps={{ maxLength: 120 }}
            label={mandatory(t("TherapyName"))}
            {...methods.register(labels.therapyName, {
              required: t("EmptyField"),
            })}
            error={!!methods.errors?.[labels.therapyName]}
            helperText={methods.errors?.[labels.therapyName]?.message}
            variant="outlined"
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function Title({ string = "" }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (string == "") return null;

  return (
    <Typography
      variant={isMobile ? titleStyles.fontSizeMobile : titleStyles.fontSize}
      fontWeight={600}
      sx={{ wordBreak: "break-word" }}
    >
      {t(string)}
    </Typography>
  );
}
